import { createAsyncThunk, createSlice, current, isPending } from "@reduxjs/toolkit";
import { apiRequest } from "services/ServiceUtil";
import { LIBRARY_SERVICE_METHOD, USER_PLAN_SERVICE_METHOD, USER_SERVICES_METHOD } from "services/commonService";
import { PAGE_SIZE } from "utils/CommonConstants";

const SLICE = "IndividualProperty";

const initialState: any = {
  propertyInfo: {
    overdueMyPlansCount: 0,
    overdueDeadlinePlansCount: 0
  },
  propertyPlans: {
    snapshot: {
      PROPERTY_CARDS_SNAPSHOT: [],
      PROPERTY_PLAN_SNAPSHOT: []
    },
    list: [],
    totalRecords: 0
  },
  deadlineAndInitiativesPlans: {
    list: [],
    totalRecords: 0
  },
  members: {
    snapshot: {},
    list: []
  },
  showIndividualPropertyLoader: false
};

export const fetchPropertyInfoAsync = createAsyncThunk(`${SLICE}/fetchPropertyInfoAsync`, (data: any, thunkApi) =>
  apiRequest(LIBRARY_SERVICE_METHOD.getPropertyInfo, {}, thunkApi, [], data)
);

export const fetchMembers = createAsyncThunk(`${SLICE}/fetchMembers`, (data: any, thunkApi) =>
  apiRequest(USER_PLAN_SERVICE_METHOD.postAbovePropertyMembers, data, thunkApi)
);

export const fetchMembersSnapshot = createAsyncThunk(`${SLICE}/fetchMembersSnapshot`, (data: any, thunkApi) =>
  apiRequest(USER_PLAN_SERVICE_METHOD.postAbovePropertyMembersSnapshot, data, thunkApi)
);

export const fetchDeadlineAndInitiativesPlans = createAsyncThunk(
  `${SLICE}/fetchDeadlineAndInitiativesPlans`,
  (data: any, thunkApi) =>
    apiRequest(LIBRARY_SERVICE_METHOD.postPlans, data, thunkApi, [], { externalHltId: data?.externalHltId || "" })
);

export const fetchPropertySnapshots = createAsyncThunk(`${SLICE}/fetchPropertySnapshots`, (data: any, thunkApi) =>
  apiRequest(USER_PLAN_SERVICE_METHOD.postAbovePropertyPropertySnapshot, data, thunkApi)
);

export const fetchPropertyPlans = createAsyncThunk(`${SLICE}/fetchPropertyPlans`, (data: any, thunkApi) =>
  apiRequest(USER_PLAN_SERVICE_METHOD.postAbovePropertyPlans, data.payload, thunkApi, [], {
    page: data.page,
    size: PAGE_SIZE
  })
);

export const fetchPropertyPlansCounts = createAsyncThunk(`${SLICE}/fetchPropertyPlansCounts`, (data: any, thunkApi) =>
  apiRequest(USER_PLAN_SERVICE_METHOD.postAbovePropertyPlans, data.payload, thunkApi, [], {
    page: data.page,
    size: PAGE_SIZE
  })
);

export const fetchUserDetails = createAsyncThunk(`${SLICE}/fetchUserDetails`, (data: any, thunkApi) =>
  apiRequest(USER_SERVICES_METHOD.getAssociates, {}, thunkApi, [], data)
);

function isRejectedAction(action: any) {
  return action.type.endsWith("rejected");
}

export const IndividualPropertySlice = createSlice({
  name: SLICE,
  initialState,
  reducers: {
    reset: (state) => {
      return { ...initialState, propertyInfo: current(state).propertyInfo };
    },
    setSnapshots: (state, action) => {
      state.propertyPlans.snapshot.PROPERTY_CARDS_SNAPSHOT = action.payload.cardSnapshot;
      state.propertyPlans.snapshot.PROPERTY_PLAN_SNAPSHOT = action.payload.planSnapshpt;
    },
    setPropertyList: (state, action) => {
      state.propertyPlans.list = action.payload;
    },
    setPropertyTotalCount: (state, action) => {
      state.propertyPlans.totalRecords = action.payload;
    },
    setOverduePropertyCount: (state, action) => {
      state.propertyInfo.overdueDeadlinePlansCount = action.payload.overdueDeadlinePlansCount;
      state.propertyInfo.overdueMyPlansCount = action.payload.overdueMyPlansCount;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMembers.fulfilled, (state, action: any) => {
        state.members.list = action.payload.members;
        state.showIndividualPropertyLoader = false;
      })
      .addCase(fetchMembersSnapshot.fulfilled, (state, action: any) => {
        state.members.snapshot = action.payload.PROPERTY_MEMBERS_SNAPSHOT?.[0] || {};
      })
      .addCase(fetchPropertyInfoAsync.fulfilled, (state, action: any) => {
        const { status, ...response } = action.payload;
        state.propertyInfo = response;
      })
      .addCase(fetchDeadlineAndInitiativesPlans.pending, (state, action: any) => {
        state.showIndividualPropertyLoader = action.meta.arg.pagination.page === 1;
      })
      .addCase(fetchDeadlineAndInitiativesPlans.fulfilled, (state, action: any) => {
        state.showIndividualPropertyLoader = false;
        state.deadlineAndInitiativesPlans.totalRecords = action?.payload?.meta?.page?.total;
        state.deadlineAndInitiativesPlans.list =
          action.meta.arg.pagination.page === 1
            ? action.payload.plans
            : [...state.deadlineAndInitiativesPlans.list, ...action.payload.plans];
      })
      .addCase(fetchPropertySnapshots.pending, (state, action: any) => {
        state.showIndividualPropertyLoader = true;
      })
      .addCase(fetchUserDetails.pending, (state, action: any) => {
        state.showIndividualPropertyLoader = true;
      })
      .addCase(fetchUserDetails.fulfilled, (state, action: any) => {
        state.showIndividualPropertyLoader = false;
      })
      .addCase(fetchPropertyPlans.pending, (state, action: any) => {
        if (action?.meta?.arg?.page === 0) state.showIndividualPropertyLoader = true;
      })
      .addCase(fetchPropertyPlans.fulfilled, (state, action) => {
        state.showIndividualPropertyLoader = false;
      })
      .addMatcher(isPending(fetchMembers, fetchMembersSnapshot, fetchPropertyInfoAsync), (state) => {
        state.showIndividualPropertyLoader = true;
      })
      .addMatcher(isRejectedAction, (state) => {
        state.showIndividualPropertyLoader = false;
      });
  }
});

export default IndividualPropertySlice.reducer;
export const { reset, setSnapshots, setPropertyList, setPropertyTotalCount, setOverduePropertyCount } =
  IndividualPropertySlice.actions;
